import {
  Identifiable,
  OrganizationalUnit,
  OrganizationalUnitsFilter,
  organizationalUnit as organizationalUnitMock,
  Resource,
  resource as resourceMock,
  role as roleMock,
  ResourceTypes,
} from "@planzone/dto";
import { HttpClient, HttpMethods } from "../types/httpClient";

type LinkedSubordinate = {
  uuid: string;
  name: string;
  avatarUrl: string | null;
  type: ResourceTypes;
  profession: string | null;
};

type LinkedResourceItem = {
  uuid: string;
  name: string;
  type: ResourceTypes;
  profession: string | null;
  avatarUrl: string | null;
  deactivated: boolean | null;
  invitationAccepted: boolean;
  tags: string[];
  role: { uuid: string; name: string } | null;
  attributes: { [key: string]: string };
  organizationalUnit: { uuid: string; name: string } | null;
  subordinates: LinkedSubordinate[];
};

type LinkedOrganizationalUnit = {
  uuid: string;
  name: string;
  rankKey: string;
  parentUuid: string | null;
  hasChildren: boolean;
  resources: LinkedResourceItem[];
};

type Response = {
  _embedded: {
    organizationalUnits: LinkedOrganizationalUnit[];
  };
  page: {
    size: number;
    totalElements: number;
    totalPages: number;
    number: number;
  };
};

function toResource(item: LinkedResourceItem): Identifiable<Resource> {
  return {
    ...resourceMock,
    uuid: item.uuid,
    name: item.name,
    type: item.type,
    profession: item.profession ?? "",
    avatarUrl: item.avatarUrl ?? "",
    deactivated: item.deactivated ?? false,
    invitationAccepted: item.invitationAccepted,
    tags: item.tags.map((name) => ({ name })),
    role: item.role
      ? { ...roleMock, uuid: item.role.uuid, name: item.role.name }
      : undefined,
    attributes: item.attributes,
    organizationalUnit: item.organizationalUnit
      ? { ...organizationalUnitMock, ...item.organizationalUnit }
      : null,
    subordinates: item.subordinates.map((subordinate) => ({
      ...resourceMock,
      ...subordinate,
      avatarUrl: subordinate.avatarUrl ?? "",
      profession: subordinate.profession ?? "",
    })),
  };
}

function getOrganizationalUnits(httpClient: HttpClient) {
  return (filter: OrganizationalUnitsFilter) => {
    const includedOrganizationalUnits = (filter.organizationalUnits ?? [])
      .filter(({ isExcluded }) => !isExcluded)
      .map(({ uuid }) => uuid);
    const excludedOrganizationalUnits = (filter.organizationalUnits ?? [])
      .filter(({ isExcluded }) => isExcluded)
      .map(({ uuid }) => uuid);

    return httpClient
      .sendHttpRequest<Response>({
        endpoint: "/organizational-units/tree",
        method: HttpMethods.POST,
        data: {
          parentUuid: filter.parentUuid || undefined,
          page: filter.page,
          size: filter.size,
          ...(includedOrganizationalUnits.length > 0 && {
            organizationalUnitUuids: includedOrganizationalUnits,
            ...(excludedOrganizationalUnits.length > 0 && {
              excludedOrganizationalUnitUuids: excludedOrganizationalUnits,
            }),
          }),
          ...(filter.resourceTags &&
            filter.resourceTags.length > 0 && {
              resourceTags: filter.resourceTags.map(({ name }) => name),
            }),
          ...(filter.roles &&
            filter.roles.length > 0 && {
              roleUuids: filter.roles.map(({ uuid }) => uuid),
            }),
          ...(filter.resourceStatuses?.length === 1 && {
            deactivated: filter.resourceStatuses[0] === "deactivated",
          }),
          ...(((filter.resources && filter.resources.length > 0) ||
            (filter.groups && filter.groups.length > 0)) && {
            resourceUuids: [
              ...(filter.resources ?? []).map(({ uuid }) => uuid),
              ...(filter.groups ?? []).reduce<string[]>(
                (resources, group) => [
                  ...resources,
                  ...(group.resources as Identifiable<Resource>[]).map(
                    ({ uuid }) => uuid,
                  ),
                ],
                [],
              ),
            ],
          }),
        },
      })
      .then(({ _embedded, page }) => ({
        organizationalUnits: _embedded.organizationalUnits.map(
          (unit): Identifiable<OrganizationalUnit> => ({
            uuid: unit.uuid,
            name: unit.name,
            rankKey: unit.rankKey,
            parentUuid: unit.parentUuid ?? undefined,
            hasChildren: unit.hasChildren,
            resources: unit.resources.map(toResource),
          }),
        ),
        totalPages: page.totalPages || 1,
      }))
      .catch((e) => Promise.reject({ ...e, message: e.detail || "" }));
  };
}

export { getOrganizationalUnits };
